/**
 * useApi Hook for HeatCraftHP
 * Provides declarative API request handling with loading, error, and data states
 * Supports automatic and manual execution, retries, and cleanup on unmount
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { createCancellation } from '../services/apiClient';
import { RequestCancelledError, isApiError } from '../services/apiErrors';

/**
 * @typedef {Object} UseApiOptions
 * @property {boolean} [immediate=false] - Execute immediately on mount
 * @property {any[]} [dependencies=[]] - Dependencies to trigger re-execution (only if immediate=true)
 * @property {boolean} [suppressNotification=false] - Don't show error notifications
 * @property {Function} [onSuccess] - Callback on successful request
 * @property {Function} [onError] - Callback on failed request
 * @property {any} [initialData=null] - Initial data value
 * @property {number} [retryCount=0] - Number of retries on failure
 * @property {number} [retryDelay=1000] - Delay between retries in ms
 */

/**
 * @typedef {Object} UseApiReturn
 * @property {any} data - Response data
 * @property {Error|null} error - Error object if failed
 * @property {boolean} loading - Loading state
 * @property {boolean} isSuccess - Whether last request succeeded
 * @property {boolean} isError - Whether last request failed
 * @property {Function} execute - Manually trigger the request
 * @property {Function} reset - Reset state to initial values
 * @property {Function} setData - Manually set data
 */

/**
 * Hook for making API requests with state management
 * @param {Function} apiFunction - API function that returns a promise (e.g., () => api.get('/users'))
 * @param {UseApiOptions} [options={}] - Options
 * @returns {UseApiReturn}
 */
export function useApi(apiFunction, options = {}) {
  const {
    immediate = false,
    dependencies = [],
    suppressNotification = false,
    onSuccess,
    onError,
    initialData = null,
    retryCount = 0,
    retryDelay = 1000,
  } = options;

  const [data, setData] = useState(initialData);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  // Track mount status
  const isMountedRef = useRef(true);
  // Cancellation controller
  const cancellationRef = useRef(null);
  // Retry count tracker
  const retriesRef = useRef(0);

  /**
   * Cancel any pending request
   */
  const cancelRequest = useCallback(() => {
    if (cancellationRef.current) {
      cancellationRef.current.cancel();
      cancellationRef.current = null;
    }
  }, []);

  /**
   * Execute the API request
   * @param {...any} args - Arguments to pass to the API function
   * @returns {Promise<any>} Response data
   */
  const execute = useCallback(async (...args) => {
    // Cancel any pending request
    cancelRequest();

    // Create new cancellation controller
    const { signal, cancel } = createCancellation();
    cancellationRef.current = { cancel };

    setLoading(true);
    setError(null);
    setIsSuccess(false);
    setIsError(false);
    retriesRef.current = 0;

    const attemptRequest = async () => {
      try {
        // Call the API function with signal and args
        const result = await apiFunction(...args, { 
          signal, 
          suppressNotification 
        });

        if (isMountedRef.current) {
          setData(result);
          setIsSuccess(true);
          setLoading(false);
          onSuccess?.(result);
        }

        return result;
      } catch (err) {
        // Ignore cancelled requests
        if (err instanceof RequestCancelledError) {
          return;
        }

        // Check if we should retry
        if (isApiError(err) && err.isRetryable && retriesRef.current < retryCount) {
          retriesRef.current += 1;
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, retryDelay * retriesRef.current));
          // Retry if still mounted
          if (isMountedRef.current) {
            return attemptRequest();
          }
        }

        // Final failure
        if (isMountedRef.current) {
          setError(err);
          setIsError(true);
          setLoading(false);
          onError?.(err);
        }

        throw err;
      }
    };

    return attemptRequest();
  }, [apiFunction, cancelRequest, suppressNotification, onSuccess, onError, retryCount, retryDelay]);

  /**
   * Reset state to initial values
   */
  const reset = useCallback(() => {
    cancelRequest();
    setData(initialData);
    setError(null);
    setLoading(false);
    setIsSuccess(false);
    setIsError(false);
  }, [cancelRequest, initialData]);

  // Immediate execution on mount or dependency change
  useEffect(() => {
    if (immediate) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate, ...dependencies]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelRequest();
    };
  }, [cancelRequest]);

  return {
    data,
    error,
    loading,
    isSuccess,
    isError,
    execute,
    reset,
    setData,
  };
}

/**
 * Hook for lazy API requests (execute must be called manually)
 * Shorthand for useApi with immediate=false
 */
export function useLazyApi(apiFunction, options = {}) {
  return useApi(apiFunction, { ...options, immediate: false });
}

/**
 * Hook for API requests that execute immediately
 * Shorthand for useApi with immediate=true
 */
export function useAutoApi(apiFunction, options = {}) {
  return useApi(apiFunction, { ...options, immediate: true });
}

/**
 * Hook for paginated API requests
 * @param {Function} apiFunction - API function that accepts { page, pageSize, ...filters }
 * @param {Object} [options={}] - Options
 * @returns {Object} Pagination state and controls
 */
export function usePaginatedApi(apiFunction, options = {}) {
  const {
    initialPage = 1,
    initialPageSize = 10,
    immediate = true,
    ...apiOptions
  } = options;

  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({});

  const {
    data,
    error,
    loading,
    execute: baseExecute,
    reset: baseReset,
    ...rest
  } = useApi(
    (params = {}) => apiFunction({ page, pageSize, ...filters, ...params }),
    {
      ...apiOptions,
      immediate: false,
      onSuccess: (result) => {
        if (result?.totalCount !== undefined) {
          setTotalCount(result.totalCount);
        }
        apiOptions.onSuccess?.(result);
      },
    }
  );

  // Extract items from paginated response
  const items = data?.items || data || [];

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = page < totalPages;
  const hasPreviousPage = page > 1;

  /**
   * Fetch with current pagination settings
   */
  const fetch = useCallback(() => {
    return baseExecute({ page, pageSize, ...filters });
  }, [baseExecute, page, pageSize, filters]);

  /**
   * Go to specific page
   */
  const goToPage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  /**
   * Change page size
   */
  const changePageSize = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page
  }, []);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1); // Reset to first page
  }, []);

  /**
   * Clear filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  /**
   * Reset everything
   */
  const reset = useCallback(() => {
    baseReset();
    setPage(initialPage);
    setPageSize(initialPageSize);
    setTotalCount(0);
    setFilters({});
  }, [baseReset, initialPage, initialPageSize]);

  // Auto-fetch when page/pageSize/filters change
  useEffect(() => {
    if (immediate) {
      fetch();
    }
  }, [page, pageSize, filters, immediate, fetch]);

  return {
    items,
    data,
    error,
    loading,
    // Pagination state
    page,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    filters,
    // Actions
    fetch,
    goToPage,
    changePageSize,
    updateFilters,
    clearFilters,
    reset,
    ...rest,
  };
}

/**
 * Hook for mutation operations (create, update, delete)
 * Provides optimistic update capabilities
 * @param {Function} mutationFunction - API function for the mutation
 * @param {Object} [options={}] - Options
 * @returns {Object} Mutation state and controls
 */
export function useMutation(mutationFunction, options = {}) {
  const {
    onSuccess,
    onError,
    onSettled,
    ...apiOptions
  } = options;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const api = useApi(mutationFunction, {
    ...apiOptions,
    immediate: false,
    onSuccess: (result) => {
      setIsSubmitting(false);
      onSuccess?.(result);
      onSettled?.(result, null);
    },
    onError: (error) => {
      setIsSubmitting(false);
      onError?.(error);
      onSettled?.(null, error);
    },
  });

  const mutate = useCallback(async (...args) => {
    setIsSubmitting(true);
    return api.execute(...args);
  }, [api]);

  return {
    ...api,
    mutate,
    isSubmitting,
  };
}

export default useApi;
